import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/env';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  AlertCircle, CheckCircle, TrendingUp, Brain, Search, 
  Lightbulb, RefreshCw, AlertTriangle, Activity
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

export default function ErrorResolution() {
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [errorOccurrences, setErrorOccurrences] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [errorForm, setErrorForm] = useState({
    ticket_id: '',
    error_code: '',
    error_message: '',
    environment: 'dev'
  });
  const [resolution, setResolution] = useState(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [kbRes, occurrencesRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/errors/knowledge`),
        axios.get(`${API}/errors/occurrences`),
        axios.get(`${API}/errors/analytics`)
      ]);

      setKnowledgeBase(kbRes.data);
      setErrorOccurrences(occurrencesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveError = async () => {
    if (!errorForm.error_code || !errorForm.error_message) {
      alert('Please provide error code and message');
      return;
    }

    setResolving(true);
    try {
      const response = await axios.post(`${API}/errors/resolve`, errorForm);
      setResolution(response.data);
    } catch (error) {
      console.error('Error resolving:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResolving(false);
    }
  };

  const markResolved = async (occurrenceId) => {
    try {
      await axios.post(`${API}/errors/occurrences/${occurrenceId}/resolve`, {
        notes: 'Resolved successfully'
      });
      alert('Error marked as resolved! Knowledge base updated 🎓');
      fetchData();
    } catch (error) {
      console.error('Error marking resolved:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  const getModuleColor = (module) => {
    const colors = {
      security: 'bg-red-500',
      sales: 'bg-blue-500',
      materials_management: 'bg-green-500',
      finance_controlling: 'bg-purple-500',
      production: 'bg-orange-500'
    };
    return colors[module] || 'bg-gray-500';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <AlertCircle className="h-8 w-8 text-red-600" />
          SAP Error Resolution
        </h1>
        <p className="text-muted-foreground mt-2">AI-powered error resolution with learning capability</p>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Errors</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.total_errors}</div>
              <p className="text-xs text-muted-foreground">Error occurrences</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Resolved</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{analytics.resolved_errors}</div>
              <p className="text-xs text-muted-foreground">{analytics.resolution_rate.toFixed(1)}% resolution rate</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Knowledge Base</CardTitle>
              <Brain className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{analytics.knowledge_base_size}</div>
              <p className="text-xs text-muted-foreground">Learned resolutions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Confidence</CardTitle>
              <TrendingUp className="h-4 w-4 text-amber-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{(analytics.average_confidence * 100).toFixed(0)}%</div>
              <p className="text-xs text-muted-foreground">AI confidence level</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="resolve" className="w-full">
        <TabsList>
          <TabsTrigger value="resolve">
            <Search className="h-4 w-4 mr-2" />
            Resolve Error
          </TabsTrigger>
          <TabsTrigger value="knowledge">
            <Brain className="h-4 w-4 mr-2" />
            Knowledge Base ({knowledgeBase.length})
          </TabsTrigger>
          <TabsTrigger value="history">
            <Activity className="h-4 w-4 mr-2" />
            Error History ({errorOccurrences.length})
          </TabsTrigger>
        </TabsList>

        {/* Resolve Error Tab */}
        <TabsContent value="resolve" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Resolve SAP Error</CardTitle>
              <CardDescription>
                Enter error details to get AI-powered resolution steps
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Error Code</Label>
                    <Input
                      value={errorForm.error_code}
                      onChange={(e) => setErrorForm({...errorForm, error_code: e.target.value})}
                      placeholder="e.g., F5 512, M7 301"
                    />
                  </div>
                  <div>
                    <Label>Environment</Label>
                    <Select value={errorForm.environment} onValueChange={(v) => setErrorForm({...errorForm, environment: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dev">Development</SelectItem>
                        <SelectItem value="qa">QA/Testing</SelectItem>
                        <SelectItem value="prod">Production</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Error Message</Label>
                  <Textarea
                    value={errorForm.error_message}
                    onChange={(e) => setErrorForm({...errorForm, error_message: e.target.value})}
                    placeholder="Paste the complete error message from SAP..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Ticket ID (Optional)</Label>
                  <Input
                    value={errorForm.ticket_id}
                    onChange={(e) => setErrorForm({...errorForm, ticket_id: e.target.value})}
                    placeholder="Link to ticket if applicable"
                  />
                </div>

                <Button onClick={resolveError} disabled={resolving} className="w-full">
                  {resolving ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing Error...
                    </>
                  ) : (
                    <>
                      <Lightbulb className="mr-2 h-4 w-4" />
                      Get Resolution Steps
                    </>
                  )}
                </Button>

                {/* Resolution Display */}
                {resolution && (
                  <div className="mt-6 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-semibold text-lg">Resolution Plan</div>
                      <div className="flex gap-2">
                        <Badge variant={resolution.source === 'learned_knowledge' ? 'default' : 'outline'}>
                          {resolution.source === 'learned_knowledge' ? '🎓 Learned' : '🤖 AI Generated'}
                        </Badge>
                        <Badge>
                          {(resolution.confidence * 100).toFixed(0)}% Confidence
                        </Badge>
                      </div>
                    </div>

                    {resolution.source === 'learned_knowledge' && (
                      <Alert className="mb-3 bg-green-50 border-green-200">
                        <AlertDescription className="text-green-800 text-sm">
                          ✓ This resolution has been used {resolution.times_used} times with {resolution.success_rate.toFixed(0)}% success rate
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <div className="font-medium text-sm">Resolution Steps:</div>
                      {resolution.steps.map((step, idx) => (
                        <div key={idx} className="flex gap-2 p-2 bg-white rounded">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </div>
                          <div className="text-sm">{step}</div>
                        </div>
                      ))}
                    </div>

                    {resolution.detailed_resolution && (
                      <details className="mt-3">
                        <summary className="text-sm font-medium cursor-pointer text-blue-600">
                          Show Detailed Analysis
                        </summary>
                        <div className="mt-2 p-3 bg-white rounded text-sm">
                          <div className="mb-2">
                            <strong>Root Cause:</strong> {resolution.detailed_resolution.root_cause_analysis}
                          </div>
                          {resolution.detailed_resolution.prevention_tips && (
                            <div>
                              <strong>Prevention:</strong>
                              <ul className="list-disc list-inside ml-2">
                                {resolution.detailed_resolution.prevention_tips.map((tip, idx) => (
                                  <li key={idx}>{tip}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Learned Error Resolutions</CardTitle>
              <CardDescription>
                AI has learned these resolutions from previous successful fixes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {knowledgeBase.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No learned resolutions yet. Resolve errors to build knowledge base.
                </div>
              ) : (
                <div className="space-y-3">
                  {knowledgeBase.map((knowledge, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                              {knowledge.error_code}
                            </code>
                            <Badge variant="outline" className="text-xs">
                              {knowledge.module || 'Unknown'}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">{knowledge.error_pattern}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Confidence</div>
                          <div className="flex items-center gap-2">
                            <Progress value={knowledge.confidence_score * 100} className="w-20 h-2" />
                            <span className="text-sm font-bold">{(knowledge.confidence_score * 100).toFixed(0)}%</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                        <div className="text-center p-2 bg-green-50 rounded">
                          <div className="font-bold text-green-600">{knowledge.success_rate.toFixed(0)}%</div>
                          <div className="text-xs text-muted-foreground">Success Rate</div>
                        </div>
                        <div className="text-center p-2 bg-blue-50 rounded">
                          <div className="font-bold text-blue-600">{knowledge.times_successful}/{knowledge.times_used}</div>
                          <div className="text-xs text-muted-foreground">Successful/Total</div>
                        </div>
                        <div className="text-center p-2 bg-purple-50 rounded">
                          <div className="font-bold text-purple-600">{knowledge.times_used}</div>
                          <div className="text-xs text-muted-foreground">Times Used</div>
                        </div>
                      </div>

                      <details>
                        <summary className="text-sm font-medium cursor-pointer text-blue-600 mb-2">
                          Show Resolution Steps ({knowledge.resolution_steps.length} steps)
                        </summary>
                        <div className="mt-2 space-y-1">
                          {knowledge.resolution_steps.map((step, stepIdx) => (
                            <div key={stepIdx} className="text-sm flex gap-2">
                              <span className="text-muted-foreground">{stepIdx + 1}.</span>
                              <span>{step}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Error History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Error Occurrence History</CardTitle>
              <CardDescription>
                All SAP errors encountered across tickets
              </CardDescription>
            </CardHeader>
            <CardContent>
              {errorOccurrences.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No errors recorded yet
                </div>
              ) : (
                <div className="space-y-3">
                  {errorOccurrences.map((occurrence) => (
                    <div key={occurrence.id} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono">
                              {occurrence.error_code}
                            </code>
                            <Badge variant={
                              occurrence.status === 'resolved' ? 'default' :
                              occurrence.status === 'failed' ? 'destructive' : 'outline'
                            }>
                              {occurrence.status}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {occurrence.environment}
                            </Badge>
                          </div>
                          <div className="text-sm">{occurrence.error_message}</div>
                        </div>
                        {occurrence.status === 'in_progress' && (
                          <Button size="sm" onClick={() => markResolved(occurrence.id)}>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Mark Resolved
                          </Button>
                        )}
                      </div>

                      {occurrence.ai_confidence && (
                        <div className="text-xs text-muted-foreground mt-2">
                          AI Confidence: {(occurrence.ai_confidence * 100).toFixed(0)}% • 
                          Occurred: {new Date(occurrence.occurred_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
